import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import AddIcon from '@/Domain/components/icons/AddIcon';
import { TrashIcon } from '@/Domain/components/icons/Trash.icon';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

type Props = {
  form: UseFormReturn<HttpActionForm>;
};

/**
 * Editable list of JSONPath expressions that whitelist which parts of the
 * HTTP response the LLM is allowed to see. Empty list means the AI sees the
 * full response (backward-compatible default). When one or more paths are
 * set, the server strips everything outside their union before the tool
 * output reaches the model.
 */
export const ResponseFilter = ({ form }: Props) => {
  const { t } = useTranslation('actions_page');

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'response_filters',
  });

  const handleAdd = () => append({ id: uuidv4(), json_path: '' });

  return (
    <div>
      <section className="relative grid h-16 grid-cols-[1fr_auto] items-center border-b border-border bg-accent px-4 text-sm font-medium text-muted-foreground">
        <span>{t('sheet.form.response_filter.table.jsonpath')}</span>
        <IconButton size="sm" onClick={handleAdd} data-testid="response-filter-add-button">
          <AddIcon />
        </IconButton>
      </section>

      {fields.length === 0 ? (
        <div className="flex flex-col gap-3 items-center justify-center mt-7">
          <p className="text-sm font-medium text-foreground">
            {t('sheet.form.response_filter.empty.title')}
          </p>
          <p className="text-center text-sm text-muted-foreground max-w-md">
            <Trans i18nKey="sheet.form.response_filter.empty.description" t={t} />
          </p>
          <Button onClick={handleAdd} data-testid="response-filter-add-empty-button">
            <AddIcon />
            {t('sheet.form.response_filter.add_button')}
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {fields.map((field, index) => (
            <div key={field.id} className="grid grid-cols-[1fr_auto] items-start gap-2 px-2">
              <FormField
                name={`response_filters.${index}.json_path`}
                render={({ field: inputField, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        data-testid={`response-filter-input-${index}`}
                        placeholder={t('sheet.form.response_filter.placeholder')}
                        error={!!fieldState.error}
                        {...inputField}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <IconButton
                size="sm"
                variant="secondary"
                onClick={() => remove(index)}
                data-testid={`response-filter-remove-${index}`}
                aria-label={t('sheet.form.response_filter.remove_aria')}
              >
                <TrashIcon />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
